import {Component, Inject, InjectionToken} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CommonError, ERROR_PARAM, ERROR_REQUEST_ID_PARAM, LocalizationService, parseUUID, RouterService} from '@npck/libs/commons';

export interface ErrorComponentConfig {
    errorIcons: Record<string, string>;
    errorsWithWhiteBackground: string[];
    errorsWithRequestId: string[];
}

export const ERROR_COMPONENT_CONFIG = new InjectionToken<ErrorComponentConfig>(
    'errorComponentConfig',
    {
        providedIn: 'root',
        factory: (): ErrorComponentConfig => ({
            errorIcons: {
                [CommonError.PAGE_NOT_FOUND]: 'ic_404',
                [CommonError.SERVER_ERROR]: 'ic_500',
                [CommonError.NETWORK_ERROR]: 'ic_error_no_network',
            },
            errorsWithWhiteBackground: [
                CommonError.SERVER_ERROR,
                CommonError.NETWORK_ERROR,
            ],
            errorsWithRequestId: [
                CommonError.PAGE_NOT_FOUND,
                CommonError.SERVER_ERROR,
                CommonError.NETWORK_ERROR,
            ],
        }),
    },
);

@Component({
    selector: 'ui-kit-error',
    templateUrl: './error.component.html',
    styleUrls: ['./error.component.scss'],
    standalone: false
})
export class ErrorComponent {

    requestId: string;
    showRequestId: boolean;
    errorIcon: string;
    errorTitle: string;
    errorSubtitle: string;
    errorCodeTitle: string;
    buttonTitle: string;
    notTransparentBackground: boolean;

    private readonly error: string;
    private readonly retryRedirectUrl: string;

    constructor(
        private localizationService: LocalizationService,
        private activatedRoute: ActivatedRoute,
        private routerService: RouterService,
        @Inject(ERROR_COMPONENT_CONFIG) readonly errorComponentConfig: ErrorComponentConfig,
    ) {
        const data = this.activatedRoute.snapshot.data as ErrorComponentData;

        const errorFromData = data.error;
        const errorFromState = this.routerService.getState<Error>(ERROR_PARAM);
        const errorFromQueryParam = this.activatedRoute.snapshot.queryParams[ERROR_PARAM];

        this.error = errorFromData || errorFromState || errorFromQueryParam || CommonError.SERVER_ERROR;

        this.retryRedirectUrl = data.onRetryRedirect;

        this.requestId = parseUUID(activatedRoute.snapshot.queryParams[ERROR_REQUEST_ID_PARAM]);
        this.showRequestId = errorComponentConfig.errorsWithRequestId.some(error => error === this.error);

        this.errorIcon = errorComponentConfig.errorIcons[this.error] || 'ic_error';

        this.errorTitle = 'i18n_lib_commonErrorTitle#' + this.error;
        this.errorSubtitle = 'i18n_lib_commonErrorSubtitle#' + this.error;
        this.errorCodeTitle = 'i18n_lib_commonErrorErrorCode';
        this.buttonTitle = 'i18n_lib_commonButtonRetry';

        this.notTransparentBackground = errorComponentConfig.errorsWithWhiteBackground.some(error => error === this.error);
    }

    retry(): void {
        this.routerService.navigate(this.retryRedirectUrl ?? this.routerService.getPreviousUrl());
    }
}

export interface ErrorComponentData {
    error?: string;
    onRetryRedirect?: string;
}
